import React, { Component, ErrorInfo, ReactNode } from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  Alert,
  AlertTitle,
  Collapse,
  IconButton
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  BugReport as BugReportIcon
} from '@mui/icons-material';
import { AppError, createSystemError, SYSTEM_ERRORS } from '@nvgc/shared';
import { errorReportingService } from '../services/errorReportingService';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: AppError, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: AppError | null;
  errorInfo: ErrorInfo | null;
  showDetails: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Convert regular Error to AppError
    const appError = createSystemError(
      SYSTEM_ERRORS.SERVER_ERROR,
      {
        originalMessage: error.message,
        stack: error.stack
      },
      'Something went wrong. Please try refreshing the page.'
    );

    return {
      hasError: true,
      error: appError
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const appError = createSystemError(
      SYSTEM_ERRORS.SERVER_ERROR,
      {
        originalMessage: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack
      },
      'Something went wrong. Please try refreshing the page.'
    );

    this.setState({
      error: appError,
      errorInfo
    });

    // Report the error
    errorReportingService.reportError(
      appError,
      errorReportingService.createContext('ErrorBoundary', 'componentDidCatch', {
        componentStack: errorInfo.componentStack
      })
    );

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(appError, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  toggleDetails = () => {
    this.setState(prev => ({ showDetails: !prev.showDetails }));
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const { error, errorInfo, showDetails } = this.state;

      return (
        <Container maxWidth="md" sx={{ mt: 4 }}>
          <Paper elevation={3} sx={{ p: 4 }}>
            <Box display="flex" alignItems="center" mb={3}>
              <BugReportIcon color="error" sx={{ mr: 2, fontSize: 40 }} />
              <Typography variant="h4" color="error">
                Oops! Something went wrong
              </Typography>
            </Box>

            <Alert severity="error" sx={{ mb: 3 }}>
              <AlertTitle>Application Error</AlertTitle>
              {error?.userMessage || 'An unexpected error occurred. Please try again.'}
            </Alert>

            <Box display="flex" gap={2} mb={3}>
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={this.handleRetry}
                color="primary"
              >
                Try Again
              </Button>
              <Button
                variant="outlined"
                onClick={this.handleReload}
              >
                Reload Page
              </Button>
            </Box>

            {/* Error Details (for development/debugging) */}
            {(process.env.NODE_ENV === 'development' || showDetails) && (
              <>
                <Box display="flex" alignItems="center" mb={2}>
                  <IconButton onClick={this.toggleDetails} size="small">
                    {showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                  <Typography variant="body2" color="textSecondary">
                    {showDetails ? 'Hide' : 'Show'} Error Details
                  </Typography>
                </Box>

                <Collapse in={showDetails}>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                    {error && (
                      <Box mb={2}>
                        <Typography variant="subtitle2" gutterBottom>
                          Error Code: {error.code}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          {error.message}
                        </Typography>
                        {error.details && (
                          <Typography variant="body2" component="pre" sx={{ fontSize: '0.75rem' }}>
                            {JSON.stringify(error.details, null, 2)}
                          </Typography>
                        )}
                      </Box>
                    )}

                    {errorInfo && (
                      <Box>
                        <Typography variant="subtitle2" gutterBottom>
                          Component Stack:
                        </Typography>
                        <Typography
                          variant="body2"
                          component="pre"
                          sx={{
                            fontSize: '0.75rem',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word'
                          }}
                        >
                          {errorInfo.componentStack}
                        </Typography>
                      </Box>
                    )}

                    {error?.stack && (
                      <Box mt={2}>
                        <Typography variant="subtitle2" gutterBottom>
                          Stack Trace:
                        </Typography>
                        <Typography
                          variant="body2"
                          component="pre"
                          sx={{
                            fontSize: '0.75rem',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word'
                          }}
                        >
                          {error.stack}
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                </Collapse>
              </>
            )}

            <Typography variant="body2" color="textSecondary" mt={3}>
              If this problem persists, please contact support with the error code above.
            </Typography>
          </Paper>
        </Container>
      );
    }

    return this.props.children;
  }
}

// Higher-order component for wrapping components with error boundary
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: Omit<Props, 'children'>
) {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
  return WrappedComponent;
}